/**
 * StoriesRing
 *
 * Compact circular celeb avatar with a pulsing orange gradient ring,
 * mounted in the Navbar (beside the brand mark). Opens a fullscreen
 * IG/TikTok-style swipeable reel of celeb shout-outs.
 *
 * Why this lives outside the hero / Live Break / Tournaments stack:
 *   The user's livestream and tournament content owns the hero. The
 *   shout-out reel needs constant discoverability without competing
 *   for that real estate, so we anchor it in the persistent navbar.
 *
 * Behaviour:
 *   - Fetches the active shout-out list once on mount + when the modal
 *     closes (so new uploads appear without a hard refresh).
 *   - Renders NOTHING when the list is empty — no broken-looking ring.
 *   - Pulses orange when at least one unseen shout-out exists for this
 *     visitor. "Seen" tracking is per-shout-out-id in localStorage so
 *     the pulse turns off once the user has actually watched them.
 *   - Works identically signed-in and signed-out (uses the public
 *     `/api/shoutouts` endpoint, no auth headers).
 *   - All network / parse errors are swallowed → returns null. The
 *     navbar must never break because of a celeb-reel issue.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { API } from "../../utils";
import { haptic } from "../../utils/nativeBridge";
import feedback from "../../utils/notificationFeedback";
import ShoutoutsReelModal from "./ShoutoutsReelModal";

const SEEN_KEY = "blk_shoutouts_seen_ids";

const readSeenIds = () => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
};

const writeSeenIds = (set) => {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set)));
  } catch {
    /* private mode — ignore */
  }
};

const StoriesRing = () => {
  const [shoutouts, setShoutouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [seenIds, setSeenIds] = useState(() => readSeenIds());
  // Wake-up animation flag — flips to true for 1s the moment a NEW
  // shout-out id appears in a refresh (mid-session arrival), then
  // auto-resets. Does not affect any click/dismiss behaviour; purely
  // a CSS class toggle so the existing flow is untouched.
  const [waking, setWaking] = useState(false);
  const mountedRef = useRef(true);
  // Snapshot of the previous fetch result so we can diff for "new" ids.
  // Initialised to null so the very first fetch never triggers a wake-up
  // (we only want the animation on mid-session arrivals, not page load).
  const prevIdsRef = useRef(null);
  const wakeTimerRef = useRef(null);

  const fetchShoutouts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shoutouts`, {
        withCredentials: false,
        timeout: 6000,
      });
      if (!mountedRef.current) return;
      const list = Array.isArray(res?.data?.shoutouts) ? res.data.shoutouts : [];
      const incomingIds = new Set(list.map((s) => s.id).filter(Boolean));

      // Only fire wake-up when:
      //   1) this is NOT the first load (prev snapshot exists), AND
      //   2) at least one incoming id is brand new compared to the snapshot.
      if (prevIdsRef.current) {
        let hasNew = false;
        for (const id of incomingIds) {
          if (!prevIdsRef.current.has(id)) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) {
          setWaking(true);
          // Multi-sensory wake-up cue — visual bump + haptic + chime.
          // All three are best-effort and silent on failure (e.g. desktop
          // browser without haptics, or audio context not yet unlocked).
          // We deliberately don't gate on isMobile — the haptic helper
          // is a no-op on web, and the chime is so light it's fine on
          // either platform; users with audio off won't hear anything.
          try {
            haptic.light?.();
          } catch {
            /* swallow */
          }
          try {
            feedback.notification?.();
          } catch {
            /* swallow */
          }
          // Clear any in-flight timer so back-to-back arrivals
          // don't leave a stale class on the element.
          if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
          wakeTimerRef.current = setTimeout(() => {
            if (mountedRef.current) setWaking(false);
          }, 1000);
        }
      }
      prevIdsRef.current = incomingIds;
      setShoutouts(list);
    } catch {
      /* swallow — ring is non-critical */
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchShoutouts();
    // Background poll — every 60s so a fresh celeb upload surfaces
    // without the user needing to refresh. Light payload (<2KB) so
    // it won't hammer the backend.
    const interval = setInterval(fetchShoutouts, 60_000);
    // Refresh on window focus too — covers "user came back from
    // another tab and wants to see fresh state immediately".
    const onFocus = () => fetchShoutouts();
    window.addEventListener("focus", onFocus);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      if (wakeTimerRef.current) clearTimeout(wakeTimerRef.current);
    };
  }, [fetchShoutouts]);

  const handleOpen = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!shoutouts.length) return;
    setOpen(true);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    // Refresh the list when the user closes the reel — they may have just
    // marked everything seen, and an admin upload during playback should
    // surface immediately too.
    fetchShoutouts();
  }, [fetchShoutouts]);

  const handleMarkSeen = useCallback((id) => {
    if (!id) return;
    setSeenIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      writeSeenIds(next);
      return next;
    });
  }, []);

  // Quick-dismiss: mark every current shout-out as seen without opening
  // the reel. Triggered by the tiny ✕ on the unseen-count badge — for
  // users who don't care about shout-outs and just want the red dot gone.
  const handleDismissAll = useCallback(
    (e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      if (!shoutouts.length) return;
      setSeenIds((prev) => {
        const next = new Set(prev);
        shoutouts.forEach((s) => {
          if (s?.id) next.add(s.id);
        });
        writeSeenIds(next);
        return next;
      });
    },
    [shoutouts],
  );

  if (!shoutouts.length) return null;

  // Lead avatar shown inside the ring — newest / first by display order.
  const lead = shoutouts[0];
  const unseenCount = shoutouts.reduce(
    (acc, s) => acc + (seenIds.has(s.id) ? 0 : 1),
    0,
  );
  const hasUnseen = unseenCount > 0;

  // ============== TIER PROGRESSION ==============
  // Visual signal that scales with how loaded the platform is with
  // shout-outs — all in BLK PACKS brand palette (amber/orange/red/gold).
  //   tier 1 (1–3 live)  → focused amber→orange pulse
  //   tier 2 (4–6 live)  → urgent amber→orange→red gradient
  //   tier 3 (7+ live)   → "enchanted": animated iridescent shimmer that
  //                        rotates through the warm-spectrum brand stops
  //                        (amber → gold → orange → red → rose) on top of
  //                        the existing red/orange box-shadow pulse.
  // Tier is derived from TOTAL active shout-outs (not unseen) so the
  // visual stays consistent for every visitor when the catalogue is hot.
  const total = shoutouts.length;
  const tier = total >= 7 ? 3 : total >= 4 ? 2 : 1;

  // Class strings built per tier so Tailwind's JIT sees them statically.
  let ringTierClass = "bg-gradient-to-tr from-gray-700 to-gray-600";
  let ringTierStyle = undefined;
  if (hasUnseen) {
    if (tier === 3) {
      // Solid placeholder for JIT; the actual gradient + shimmer live
      // in the inline style + keyframes so we can animate background-position.
      ringTierClass = "blk-ring-tier3 blk-stories-ring-pulse";
      ringTierStyle = {
        background:
          "linear-gradient(45deg, #fcd34d, #fbbf24, #f97316, #ef4444, #f43f5e, #fbbf24, #fcd34d)",
        backgroundSize: "300% 300%",
        animation:
          "blkStoriesRingPulse 2.2s ease-in-out infinite, blkRingIridescent 4.5s linear infinite",
      };
    } else if (tier === 2) {
      ringTierClass =
        "bg-gradient-to-tr from-amber-400 via-orange-500 to-red-500 blk-stories-ring-pulse";
      ringTierStyle = { animation: "blkStoriesRingPulse 2.2s ease-in-out infinite" };
    } else {
      ringTierClass =
        "bg-gradient-to-tr from-amber-300 to-orange-500 blk-stories-ring-pulse";
      ringTierStyle = { animation: "blkStoriesRingPulse 2.2s ease-in-out infinite" };
    }
  }

  return (
    <>
      {/* Wrapper — keeps the dismiss badge a SIBLING of the ring button
          (not nested inside) so we don't have a nested-interactive
          a11y violation. */}
      <span
        className="relative inline-flex flex-shrink-0"
        data-testid="stories-ring-wrapper"
        data-tier={tier}
      >
        <button
          type="button"
          onClick={handleOpen}
          data-testid="stories-ring-button"
          aria-label={`Open celebrity shout-outs (${shoutouts.length} clip${shoutouts.length === 1 ? "" : "s"})`}
          // Outer padding gives a 44pt+ tap area for iOS/Android per HIG,
          // while the visible ring stays at 36px. `touch-manipulation`
          // suppresses the 300ms tap delay on mobile WebViews (Capacitor).
          className="relative group focus:outline-none p-1.5 -mx-1.5 touch-manipulation active:scale-95 transition-transform"
        >
          {/* Scoped keyframes for the ring pulse + iridescent shimmer +
              wake-up bump. Wrapping in a scoped style block avoids
              touching tailwind.config.js for one-off animations. */}
          <style>{`
            @keyframes blkStoriesRingPulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(249, 115, 22, 0.55), 0 0 0 0 rgba(239, 68, 68, 0.0); }
              50%      { box-shadow: 0 0 0 5px rgba(249, 115, 22, 0.0),  0 0 14px 3px rgba(239, 68, 68, 0.55); }
            }
            @keyframes blkRingIridescent {
              0%   { background-position: 0% 50%; }
              50%  { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            @keyframes blkStoriesRingWake {
              0%   { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(249, 115, 22, 0)); }
              35%  { transform: scale(1.15); filter: drop-shadow(0 0 14px rgba(251, 191, 36, 0.85)); }
              70%  { transform: scale(0.98); filter: drop-shadow(0 0 8px  rgba(239, 68, 68, 0.6)); }
              100% { transform: scale(1);    filter: drop-shadow(0 0 0 rgba(249, 115, 22, 0)); }
            }
            @media (prefers-reduced-motion: reduce) {
              .blk-stories-ring-pulse,
              .blk-ring-tier3,
              .blk-stories-ring-wake { animation: none !important; }
            }
          `}</style>

          <span
            className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full p-[2px] ${ringTierClass}${
              waking ? " blk-stories-ring-wake" : ""
            }`}
            style={{
              ...(ringTierStyle || {}),
              // Apply the wake animation alongside any tier animation.
              // CSS lets us comma-chain animations from inline style, so
              // we layer the bump on top of the pulse / iridescence.
              ...(waking
                ? {
                    animation: `${
                      ringTierStyle?.animation
                        ? ringTierStyle.animation + ", "
                        : ""
                    }blkStoriesRingWake 1s ease-out`,
                  }
                : {}),
            }}
            data-testid="stories-ring-avatar"
            data-waking={waking ? "1" : "0"}
          >
            <span className="block w-full h-full rounded-full bg-[#12121e] p-[2px]">
              {lead.avatar_url ? (
                <img
                  src={lead.avatar_url}
                  alt={lead.celeb_name || "Shout-out"}
                  className="w-full h-full object-cover rounded-full"
                  loading="lazy"
                  onError={(e) => {
                    // Fall back to a brand initial circle if the avatar 404s.
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span className="flex items-center justify-center w-full h-full rounded-full bg-gradient-to-br from-orange-500/30 to-red-500/30 text-[10px] font-bold text-orange-200">
                  {(lead.celeb_name || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
            </span>
          </span>
        </button>

        {/* Dismiss badge — sibling of the ring button (NOT nested) so each
            element has a single, unambiguous interaction. Tap the badge =
            mark every current shout-out as seen and turn off the pulse,
            without opening the reel. Positioned absolutely over the
            ring's top-right corner; pointer-events-auto guarantees taps
            land on the badge first regardless of stacking context.

            Styling — refined gradient pill (not a hard red blob) so it
            reads as a polished counter, not a notification stink-bug.
            Hover/long-press on desktop swaps the number for a clear ✕. */}
        {hasUnseen && unseenCount > 0 && (
          <button
            type="button"
            onClick={handleDismissAll}
            data-testid="stories-ring-badge"
            aria-label="Dismiss unseen shout-outs without opening the reel"
            title="Tap to dismiss · long-press also works"
            className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-[5px] rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white text-[10px] font-bold leading-none flex items-center justify-center ring-2 ring-[#12121e] shadow-[0_0_8px_rgba(239,68,68,0.45)] hover:from-orange-400 hover:to-red-400 cursor-pointer transition-all group/badge pointer-events-auto touch-manipulation active:scale-90 z-10"
          >
            <span className="group-hover/badge:opacity-0 transition-opacity">
              {unseenCount > 9 ? "9+" : unseenCount}
            </span>
            {/* Desktop hover: badge content swaps to a clear ✕ icon */}
            <X
              className="absolute inset-0 m-auto w-[10px] h-[10px] opacity-0 group-hover/badge:opacity-100 transition-opacity"
              strokeWidth={3.5}
            />
          </button>
        )}
      </span>

      {open && (
        <ShoutoutsReelModal
          shoutouts={shoutouts}
          onClose={handleClose}
          onMarkSeen={handleMarkSeen}
        />
      )}
    </>
  );
};

export default StoriesRing;
